import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to observe the intersection of an element with the viewport.
 *
 * @param callback - Function to call when the element becomes visible.
 * @param options - Options for the IntersectionObserver.
 * @param runOnce - If true, the callback will only run once when the element becomes visible.
 * @returns A ref to attach to the target element and a boolean indicating visibility.
 */
const useIntersectionObserver = <T extends HTMLElement = HTMLElement>(
  callback: () => void,
  options: IntersectionObserverInit = {},
  runOnce: boolean = false,
): [React.RefObject<T>, boolean] => {
  const elementRef = useRef<T>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const hasRunRef = useRef<boolean>(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          if (runOnce && hasRunRef.current) {
            return;
          }
          callback();
          if (runOnce) {
            hasRunRef.current = true;
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1, ...options },
    );

    const currentElement = elementRef.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [callback, options, runOnce]);

  return [elementRef, isVisible];
};

export default useIntersectionObserver;
